import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import Card from '../../../../components/Card'
import { useTeam } from '../../../../config/context/team'
import { useTeamTrackingRules } from '../../../../config/queries'
import { updateTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import TrackingRules, {
  extendTrackingRules,
} from '../../../TeamDashboard/subpages/TeamRoster/components/TrackingRules'
import AddTeamMemberModal from '../../../TeamDashboard/subpages/TeamRoster/components/AddTeamMemberModal'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'
import { Button } from '@/components/ui/button'

const formSchema = z.object({
  tracking_rules_attributes: z.array(
    z.object({
      id: z.number().optional(),
      rank: z.number(),
      teamRankId: z.number().nullable().optional(),
      _destroy: z.boolean().optional(),
    }),
  ),
})

type RosterFormValues = z.infer<typeof formSchema>

interface RosterStepFormProps {
  trackingRules: wowaudit.TrackingRule[]
  goNext: () => void
  goBack: () => void
  isFirst: boolean
}

const RosterStepForm = ({
  trackingRules,
  goNext,
  goBack,
  isFirst,
}: RosterStepFormProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [addingMembers, setAddingMembers] = useState(false)

  const defaultTeamRankId =
    team.teamRanks?.find((rank) => rank.default)?.id ?? team.teamRanks?.[0]?.id

  const form = useForm<RosterFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tracking_rules_attributes: extendTrackingRules(trackingRules).map(
        (rule) => ({
          ...rule,
          teamRankId: rule.teamRankId ?? defaultTeamRankId,
        }),
      ),
    },
  })

  const onSubmit = (values: RosterFormValues) => {
    setSubmitting(true)

    const payload: Partial<wowaudit.Team> & {
      tracking_rules_attributes: RosterFormValues['tracking_rules_attributes']
    } = {
      tracking_rules_attributes: values.tracking_rules_attributes.filter(
        (rule) => rule._destroy !== undefined,
      ),
    }

    updateTeam(team.id, payload)
      .then(async (updatedTeam) => {
        queryClient.setQueryData(['team', { path: team.path }], updatedTeam)
        await queryClient.invalidateQueries({
          queryKey: ['teamTrackingRules', { id: team.id }],
        })
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <StepLayout
      icon={Users}
      title='Build your roster'
      description='Specify which characters should be added to your team automatically, based on their guild rank.'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card className='pb-6'>
            <TrackingRules />
          </Card>

          <p className='mt-4 text-center'>
            <Button
              variant='outline'
              onClick={() => setAddingMembers(true)}
              type='button'
            >
              Or add characters manually
            </Button>
          </p>

          <StepFooter
            showBack={!isFirst}
            onBack={goBack}
            onContinue={form.handleSubmit(onSubmit)}
            continueLoading={submitting}
            onSkip={goNext}
          />
        </form>
      </Form>

      {addingMembers && (
        <AddTeamMemberModal
          open={addingMembers}
          setOpen={setAddingMembers}
          hideTrackingRulesTab
        />
      )}
    </StepLayout>
  )
}

const RosterStep = ({ goNext, goBack, isFirst }: StepProps) => {
  const { team } = useTeam()
  const { data: trackingRules, isPending } = useTeamTrackingRules(team.id)

  if (isPending) {
    return <Skeleton className='h-200' />
  }

  return (
    <RosterStepForm
      trackingRules={trackingRules ?? []}
      goNext={goNext}
      goBack={goBack}
      isFirst={isFirst}
    />
  )
}

export default RosterStep
